import { useEffect, useState } from "react";
import { Activity, MessageSquarePlus, Pause, Play, SkipForward, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  configureSimulation,
  getSimulationState,
  injectDemoMessage,
  setSimulationSpeed,
  shouldAutoStart,
  startSimulation,
  stepSimulation,
  stopSimulation,
  subscribeSimulation,
  type SimState,
} from "@/lib/demo-simulation";


const SPEEDS = [
  { label: "Fast", ms: 8000 },
  { label: "Normal", ms: 20000 },
  { label: "Slow", ms: 60000 },
];

/**
 * Floating control for the demo clinic-day simulation. Lets you run, pause,
 * step and change the pace of the automated queue / notification activity.
 */
export default function DemoSimulator() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<SimState>(getSimulationState());

  useEffect(() => subscribeSimulation(setState) as unknown as () => void, []);

  useEffect(() => {
    configureSimulation(user?.id ?? null, () => {
      qc.invalidateQueries({ queryKey: ["appointments"] });
      qc.invalidateQueries({ queryKey: ["notification-center"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      qc.invalidateQueries({ queryKey: ["queue"] });
      qc.invalidateQueries({ queryKey: ["inbox-messages"] });
    });

    if (user && shouldAutoStart()) startSimulation();
    return () => stopSimulation();
  }, [user, qc]);

  if (!user) return null;

  return (
    <div className="fixed bottom-24 left-4 z-40 md:bottom-6">
      {open ? (
        <div className="w-[19rem] rounded-2xl border bg-card/95 p-4 shadow-xl backdrop-blur">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className={cn("h-4 w-4 text-primary", state.running && "animate-pulse")} />
              <span className="text-sm font-semibold">Clinic day simulation</span>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setOpen(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <p className="mb-3 text-xs text-muted-foreground">
            Automatically calls in patients, completes consultations, registers walk-ins, drops in
            WhatsApp/SMS messages and raises live notifications — so the app behaves like a real
            clinic day. Reply to a thread and the patient will answer back.
          </p>

          <div className="mb-3 flex gap-2">
            {state.running ? (
              <Button size="sm" variant="secondary" className="flex-1" onClick={stopSimulation}>
                <Pause className="mr-1.5 h-3.5 w-3.5" /> Pause
              </Button>
            ) : (
              <Button size="sm" className="flex-1" onClick={startSimulation}>
                <Play className="mr-1.5 h-3.5 w-3.5" /> Start
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={stepSimulation}>
              <SkipForward className="mr-1.5 h-3.5 w-3.5" /> Step
            </Button>
          </div>

          <Button
            size="sm"
            variant="outline"
            className="mb-3 w-full justify-center text-xs"
            onClick={injectDemoMessage}
          >
            <MessageSquarePlus className="mr-1.5 h-3.5 w-3.5" /> Inject WhatsApp/SMS message
          </Button>


          <div className="mb-3 flex gap-1.5">
            {SPEEDS.map((s) => (
              <Button
                key={s.ms}
                size="sm"
                variant={state.intervalMs === s.ms ? "default" : "outline"}
                className="h-7 flex-1 text-xs"
                onClick={() => setSimulationSpeed(s.ms)}
              >
                {s.label}
              </Button>
            ))}
          </div>

          <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
            <span>{state.ticks} steps</span>
            <Badge variant={state.running ? "default" : "secondary"} className="text-[10px]">
              {state.running ? "Live" : "Paused"}
            </Badge>
          </div>

          <div className="max-h-40 space-y-1.5 overflow-y-auto">
            {state.events.length === 0 ? (
              <p className="text-xs text-muted-foreground">No activity yet.</p>
            ) : (
              state.events.map((e) => (
                <div key={e.id} className="rounded-lg bg-muted/60 px-2 py-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-medium">{e.label}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(e.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  <p className="truncate text-[11px] text-muted-foreground">{e.detail}</p>
                </div>
              ))
            )}
          </div>

          {state.lastError && (
            <p className="mt-2 text-[11px] text-destructive">{state.lastError}</p>
          )}
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open clinic day simulation"
          className={cn(
            "flex h-11 items-center gap-2 rounded-full border bg-card/95 px-3 text-xs font-medium shadow-lg backdrop-blur transition hover:bg-accent",
            state.running && "border-primary/50 text-primary",
          )}
        >
          <Activity className={cn("h-4 w-4", state.running && "animate-pulse")} />
          {state.running ? "Simulating" : "Demo sim"}
        </button>
      )}
    </div>
  );
}
